import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { HandCoins, Loader2, Receipt, X } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { Section, Badge, Button, Input, Select, Field, EmptyState } from "@/components/premium/UI";

// ---- helpers ---------------------------------------------------------------
const fmt = (n, ccy) => {
  const v = Number(n || 0);
  const s = v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return ccy === "USD" ? `$${s}` : `R${s}`;
};
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

/**
 * Outstanding multi-currency credit sales panel.
 * Sourced from /api/sales/outstanding-mc (authoritative Sales & Cash).
 * Records subsequent (partial) payments via POST /sales/transactions/{id}/payments.
 * Birds are NOT depleted here — the sale already happened.
 */
export default function OutstandingMc() {
  const { has } = usePermissions();
  const canWrite = has("data:poultry_sales:write");

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ count: 0, total_by_currency: { ZAR: 0, USD: 0 }, items: [] });
  const [selected, setSelected] = useState(null); // tx currently being paid
  const [rate, setRate] = useState(null);

  async function reload() {
    setLoading(true);
    try {
      const [o, er] = await Promise.all([
        api.get("/sales/outstanding-mc"),
        api.get("/sales/exchange-rates").catch(() => ({ data: [] })),
      ]);
      setData(o.data || { count: 0, total_by_currency: { ZAR: 0, USD: 0 }, items: [] });
      const usdZar = (er.data || []).find(r => r.from_currency === "USD" && r.to_currency === "ZAR" && r.active !== false);
      setRate(usdZar || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load outstanding sales");
    } finally { setLoading(false); }
  }
  useEffect(() => { reload(); }, []);

  return (
    <div className="space-y-5" data-testid="outstanding-mc-panel">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-xl border border-border p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Open credit sales</div>
          <div className="mt-1 font-heading text-3xl font-bold tracking-tight" data-testid="out-count">{data.count}</div>
        </div>
        <div className="rounded-xl border border-border p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Outstanding (ZAR sales)</div>
          <div className="mt-1 font-heading text-3xl font-bold tracking-tight" data-testid="out-total-zar">{fmt(data.total_by_currency?.ZAR, "ZAR")}</div>
        </div>
        <div className="rounded-xl border border-border p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Outstanding (USD sales)</div>
          <div className="mt-1 font-heading text-3xl font-bold tracking-tight" data-testid="out-total-usd">{fmt(data.total_by_currency?.USD, "USD")}</div>
        </div>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-2">{[1, 2, 3].map(i => <div key={i} className="h-12 bg-muted/40 rounded-lg" />)}</div>
      ) : data.items.length === 0 ? (
        <EmptyState icon={HandCoins} title="No outstanding credit sales" description="All multi-currency sales are fully paid." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm" data-testid="out-mc-table">
            <thead className="bg-muted/40 text-[11px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Invoice</th>
                <th className="text-left px-3 py-2">Batch</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-right px-3 py-2">Birds</th>
                <th className="text-right px-3 py-2">Total</th>
                <th className="text-right px-3 py-2">Paid</th>
                <th className="text-right px-3 py-2">Outstanding</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => {
                const paid = round2((r.paid_to_date != null ? r.paid_to_date : ((r.settlement_equivalent || {}).amount || 0)));
                const status = String(r.payment_status || "").toUpperCase();
                return (
                  <tr key={r.id} className="border-t border-border hover:bg-muted/20" data-testid={`out-row-${r.invoice_number}`}>
                    <td className="px-3 py-2 whitespace-nowrap">{r.sale_date}</td>
                    <td className="px-3 py-2 font-mono">{r.invoice_number}</td>
                    <td className="px-3 py-2">{r.poultry_batch_number || "—"}</td>
                    <td className="px-3 py-2">{r.customer || "—"}</td>
                    <td className="px-3 py-2 text-right">{r.quantity}</td>
                    <td className="px-3 py-2 text-right font-semibold">{fmt(r.total, r.transaction_currency)}</td>
                    <td className="px-3 py-2 text-right">{fmt(paid, r.transaction_currency)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-destructive">{fmt(r.balance, r.transaction_currency)}</td>
                    <td className="px-3 py-2"><Badge variant={status === "PAID" ? "success" : status === "PARTIALLY_PAID" ? "pending" : "critical"}>{status || "OUTSTANDING"}</Badge></td>
                    <td className="px-3 py-2 text-right">
                      {canWrite && (
                        <Button size="sm" variant="primary" onClick={() => setSelected(r)} data-testid={`out-record-payment-${r.invoice_number}`}>
                          <Receipt className="w-3.5 h-3.5" /> Record payment
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <PaymentModal
          tx={selected}
          rate={rate}
          onClose={() => setSelected(null)}
          onSaved={() => { setSelected(null); reload(); }}
        />
      )}
    </div>
  );
}

// ---- Payment modal ---------------------------------------------------------
function PaymentModal({ tx, rate, onClose, onSaved }) {
  const [ccy, setCcy] = useState(tx.transaction_currency || "ZAR");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");
  const [zarChange, setZarChange] = useState("");
  const [usdChange, setUsdChange] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const rateUsdZar = rate?.rate ? Number(rate.rate) : null;
  const outstanding = Number(tx.balance || 0);

  // Convert entered payment/change into tx currency to preview new balance
  const preview = useMemo(() => {
    const toTx = (val, from) => {
      const v = Number(val || 0);
      if (!v) return 0;
      if (from === tx.transaction_currency) return v;
      if (!rateUsdZar) return 0;
      if (tx.transaction_currency === "ZAR" && from === "USD") return v * rateUsdZar;
      if (tx.transaction_currency === "USD" && from === "ZAR") return v / rateUsdZar;
      return v;
    };
    const pay = toTx(amount, ccy);
    const chg = toTx(zarChange, "ZAR") + toTx(usdChange, "USD");
    const net = round2(pay - chg);
    const newBalance = round2(outstanding - net);
    return { pay, chg, net, newBalance };
  }, [amount, ccy, zarChange, usdChange, outstanding, rateUsdZar, tx.transaction_currency]);

  async function submit() {
    if (Number(amount) <= 0) { toast.error("Enter an amount greater than zero."); return; }
    setSubmitting(true);
    try {
      const body = {
        currency: ccy,
        amount: Number(amount),
        method,
        reference,
        change_returned: [
          ...(Number(zarChange) > 0 ? [{ currency: "ZAR", amount: Number(zarChange) }] : []),
          ...(Number(usdChange) > 0 ? [{ currency: "USD", amount: Number(usdChange) }] : []),
        ],
      };
      const { data } = await api.post(`/sales/transactions/${tx.id}/payments`, body);
      toast.success(`Payment recorded. New status: ${data.payment_status}`);
      onSaved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment could not be recorded.");
    } finally { setSubmitting(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="payment-modal" onClick={onClose}>
      <div className="relative w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <Section title={`Record payment — ${tx.invoice_number}`} description={`Outstanding: ${fmt(tx.balance, tx.transaction_currency)} · Customer: ${tx.customer || "—"}`}
          actions={<Button variant="ghost" size="sm" onClick={onClose} data-testid="payment-modal-close"><X className="w-4 h-4" /></Button>}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Currency received">
              <Select value={ccy} onChange={e => setCcy(e.target.value)} data-testid="payment-ccy">
                <option value="ZAR">ZAR</option>
                <option value="USD">USD</option>
              </Select>
            </Field>
            <Field label="Amount">
              <Input type="number" min={0} step="0.01" value={amount} onChange={e => setAmount(e.target.value)} data-testid="payment-amount" placeholder={ccy === "USD" ? "$0.00" : "R0.00"} />
            </Field>
            <Field label="Method">
              <Select value={method} onChange={e => setMethod(e.target.value)} data-testid="payment-method">
                <option value="cash">Cash</option>
                <option value="bank">Bank</option>
                <option value="mobile">Mobile</option>
                <option value="other">Other</option>
              </Select>
            </Field>
            <Field label="Reference (optional)">
              <Input value={reference} onChange={e => setReference(e.target.value)} data-testid="payment-reference" />
            </Field>
          </div>

          {preview.pay > outstanding + 0.005 && (
            <div className="mt-3 p-3 rounded-xl border border-warning/40 bg-warning/5" data-testid="payment-change-panel">
              <div className="text-xs font-semibold text-warning mb-2">Tender exceeds outstanding balance. Record change physically returned:</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Change returned in ZAR">
                  <Input type="number" min={0} step="0.01" value={zarChange} onChange={e => setZarChange(e.target.value)} data-testid="payment-zar-change" placeholder="R0.00" />
                </Field>
                <Field label="Change returned in USD">
                  <Input type="number" min={0} step="0.01" value={usdChange} onChange={e => setUsdChange(e.target.value)} data-testid="payment-usd-change" placeholder="$0.00" />
                </Field>
              </div>
            </div>
          )}

          <div className="mt-4 grid grid-cols-3 gap-3">
            <div className="rounded-xl border border-border p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Outstanding</div>
              <div className="text-lg font-heading font-bold" data-testid="payment-outstanding">{fmt(outstanding, tx.transaction_currency)}</div>
            </div>
            <div className="rounded-xl border border-border p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Net applied</div>
              <div className="text-lg font-heading font-bold" data-testid="payment-net">{fmt(preview.net, tx.transaction_currency)}</div>
            </div>
            <div className="rounded-xl border border-border p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">New balance</div>
              <div className={`text-lg font-heading font-bold ${Math.abs(preview.newBalance) < 0.005 ? "text-success" : preview.newBalance > 0 ? "text-destructive" : "text-warning"}`} data-testid="payment-new-balance">
                {fmt(Math.abs(preview.newBalance), tx.transaction_currency)}{preview.newBalance > 0.005 ? " outstanding" : preview.newBalance < -0.005 ? " over" : ""}
              </div>
            </div>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={submit} disabled={submitting || Number(amount) <= 0} data-testid="payment-submit">
              {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Recording…</>) : "Record payment"}
            </Button>
          </div>

          <div className="mt-3 text-[11px] text-muted-foreground italic">
            Birds are not affected. The physical sale already depleted the batch at capture time. Only cash and outstanding balance update here.
          </div>
        </Section>
      </div>
    </div>
  );
}
